import type { Rect } from '../core/types';
import { imageBlobFromClipboard, loadImage, type LoadedImage } from './import';

export interface ClipboardImage {
  /** Document-space box the pixels were taken from; Paste in Place needs it. */
  rect: Rect;
  data: Uint8Array;
}

/**
 * The in-app clipboard. The system clipboard only carries a flat PNG, so keeping
 * our own copy is what lets Paste in Place restore the original position and
 * avoids a permission prompt for the common copy-then-paste case.
 */
let internalImage: ClipboardImage | null = null;

/**
 * Byte size of the PNG we last mirrored onto the system clipboard. It is how we
 * recognise our own copy later: anything else means another app has copied
 * something since, and that newer content has to win over `internalImage`.
 */
let mirroredSize: number | null = null;

export function setInternalClipboard(image: ClipboardImage | null): void {
  internalImage = image;
  mirroredSize = null;
}

export function getInternalClipboard(): ClipboardImage | null {
  return internalImage;
}

/** Mirrors the copy onto the system clipboard so other apps can use it. */
export async function writeImageToSystemClipboard(image: ClipboardImage): Promise<boolean> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false;
  try {
    const blob = await encodePng(image);
    if (!blob) return false;
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    mirroredSize = blob.size;
    return true;
  } catch {
    // Denied permission or an unfocused document; the internal clipboard still works.
    return false;
  }
}

export interface SystemClipboardImage {
  image: LoadedImage;
  /** Encoded byte size, matched against `mirroredSize` to spot our own copy. */
  size: number;
}

/** Reads an image another app put on the clipboard, or `null` when there is none. */
export async function readImageFromSystemClipboard(): Promise<SystemClipboardImage | null> {
  if (!navigator.clipboard?.read) return null;
  try {
    const blob = await imageBlobFromClipboard(await navigator.clipboard.read());
    if (!blob) return null;
    return { image: await loadImage(blob, 'Clipboard'), size: blob.size };
  } catch {
    // No permission, an unfocused document, or nothing readable on the clipboard.
    return null;
  }
}

/** True when the system clipboard still holds the copy this app put there. */
export function isOwnMirror(size: number): boolean {
  return mirroredSize !== null && mirroredSize === size;
}

async function encodePng(image: ClipboardImage): Promise<Blob | null> {
  const { width, height } = image.rect;
  const bitmap = new ImageData(new Uint8ClampedArray(image.data), width, height);

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    canvas.getContext('2d')!.putImageData(bitmap, 0, 0);
    return await canvas.convertToBlob({ type: 'image/png' });
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.putImageData(bitmap, 0, 0);
  return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}
